import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SquarePen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { usePEClinicIds } from '@/hooks/pe/usePEClinicIds.ts';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import PaginationComponent from '@/components/common/PaginationComponent';
import StatusSelectionComponent from '@/components/common/StatusSelectionComponent';
import { badgeStyles, getCCMStatusStyle, getRPMStatusStyle } from '@/utils/badgeStyles';
import { serviceFactory } from '@/services/core/serviceFactory';
import { PatientEnrollerService, type PEPatient as ServicePEPatient } from '@/services/features/PatientEnrollerService';
import { convertUTCToTimezone, getTimezoneAbbreviation } from '@/utils/timezoneUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';

// Use the service interface
type PEPatient = ServicePEPatient;

interface StatusOption {
  id: number;
  status: string;
}

interface PEPatientListProps {
  type: 'followup' | 'deferred';
  onBack?: () => void;
  onPatientSelect?: (patientClinicDataId: number, clinicId?: number) => void;
}

const PEPatientList: React.FC<PEPatientListProps> = ({ type }) => {
  const { user, hasuraToken } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { clinicIds, isLoading: clinicIdsLoading } = usePEClinicIds();
  const [patients, setPatients] = useState<PEPatient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  // Status selection state for multi-select functionality
  const [selectedCCMStatuses, setSelectedCCMStatuses] = useState<string[]>([]);
  const [selectedRPMStatuses, setSelectedRPMStatuses] = useState<string[]>([]);
  const [ccmStatusOptions, setCcmStatusOptions] = useState<StatusOption[]>([]);
  const [rpmStatusOptions, setRpmStatusOptions] = useState<StatusOption[]>([]);

  // Search state
  const [searchBy, setSearchBy] = useState<string>('name');
  const [searchTerm, setSearchTerm] = useState('');

  const title = type === 'followup' ? 'Follow up Patients' : 'Deferred Patients';
  const statusFilter = type === 'followup' ? 'Follow Up' : 'Deferred';

  useEffect(() => {
    if (hasuraToken) {
      loadStatusOptions();
    }
  }, [hasuraToken]);

  useEffect(() => {
    if (clinicIds.length > 0) {
      fetchPatients();
    }
  }, [type, user?.id, hasuraToken, clinicIds]);

  const loadStatusOptions = async () => {
    if (!hasuraToken) return;

    try {
      // Import statusGraphqlService
      const { statusGraphqlService } = await import('@/services/api');
      
      // Use role-based CCM status options for PE users (PE uses Global query)
      const ccmResponse = await statusGraphqlService.getCCMStatusOptionsByRole(hasuraToken, 'PE');
      setCcmStatusOptions(ccmResponse.data?.pep_v2_status_mapping_ccm || []);

      // Use role-based RPM status options for PE users (PE uses Global query)
      const rpmResponse = await statusGraphqlService.getRPMStatusOptionsByRole(hasuraToken, 'PE');
      setRpmStatusOptions(rpmResponse.data?.pep_v2_status_mapping_rpm || []);
    } catch (error) {
      console.error('Error loading status options:', error);
      // Fallback to empty arrays to prevent crashes
      setCcmStatusOptions([]);
      setRpmStatusOptions([]);
    }
  };

  const handleCCMStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedCCMStatuses(prev => [...prev, status]);
    } else {
      setSelectedCCMStatuses(prev => prev.filter(s => s !== status));
    }
  };

  const handleRPMStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedRPMStatuses(prev => [...prev, status]);
    } else {
      setSelectedRPMStatuses(prev => prev.filter(s => s !== status));
    }
  };

  const fetchPatients = async () => {
    if (!user?.id || !hasuraToken || clinicIds.length === 0) return;

    try {
      setIsLoading(true);
      setError(null);

      // Update service context with current auth info
      serviceFactory.updateGlobalContext({
        hasuraToken,
        userId: user?.id,
        token: hasuraToken, // Using hasuraToken for GraphQL queries
        userType: user?.type
      });

      const patientEnrollerService = serviceFactory.getService(PatientEnrollerService);

      const response = await patientEnrollerService.fetchPETaskPatientList(
        clinicIds,
        statusFilter
      );

      if (response.success && response.data) {
        setPatients(response.data.pep_vw_patient_enroller_task_patient_list || []);
      } else {
        throw new Error(response.errors?.join(', ') || `Failed to fetch ${type} patients`);
      }
    } catch (err) {
      console.error(`Error fetching ${type} patients:`, err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      toast({
        title: "Error",
        description: `Failed to load ${type} patients`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientAction = (patient: PEPatient) => {
    console.log('Navigating to patient details for:', patient.patient_clinic_data_id);
    navigate(`/patient-details/${patient.patient_clinic_data_id}`, {
      state: { fromPEPatientList: true, pePatientType: type }
    });
  };

  const formatAppointmentDate = (dateString: string, patientTimezone: string) => {
    if (!dateString) return 'Appointment Not Set';
    try {
      const { date, time } = convertUTCToTimezone(dateString, patientTimezone);
      const timezoneAbbr = getTimezoneAbbreviation(patientTimezone);
      return `${date} ${time} ${timezoneAbbr}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatDOB = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getFilteredPatients = () => {
    let filtered = [...patients];

    // Apply text search filters (local filtering)
    if (searchTerm && (searchBy === 'name' || searchBy === 'clinic' || searchBy === 'phone' || searchBy === 'provider')) {
      filtered = filtered.filter(patient => {
        const searchLower = searchTerm.toLowerCase();
        switch (searchBy) {
          case 'name':
            return patient.full_name?.toLowerCase().includes(searchLower);
          case 'clinic':
            return patient.clinic_name?.toLowerCase().includes(searchLower);
          case 'phone':
            return patient.phone_number?.toLowerCase().includes(searchLower);
          case 'provider':
            return patient.provider_name?.toLowerCase().includes(searchLower);
          default:
            return true;
        }
      });
    }

    // Apply date filters (local filtering) - searchTerm contains date string
    if (searchTerm && (searchBy === 'dob' || searchBy === 'appointment')) {
      if (searchBy === 'dob') {
        filtered = filtered.filter(patient => {
          if (!patient.dob) return false;
          const patientDOB = formatDOB(patient.dob);
          return patientDOB.includes(searchTerm) || searchTerm.includes(patientDOB);
        });
      }

      if (searchBy === 'appointment') {
        filtered = filtered.filter(patient => {
          if (!patient.consented_appointment_date_time) return false;
          const appointmentDate = formatAppointmentDate(patient.consented_appointment_date_time, patient.patient_clinic_timezone || 'US/Eastern');
          return appointmentDate.includes(searchTerm) || searchTerm.includes(appointmentDate);
        });
      }
    }

    // Handle single status search
    if (searchBy === 'ccm_status' && searchTerm) {
      filtered = filtered.filter(patient => patient.ccm_status_value === searchTerm);
    }

    if (searchBy === 'rpm_status' && searchTerm) {
      filtered = filtered.filter(patient => {
        if (searchTerm === 'No Status Assigned') {
          return !patient.rpm_status_value || patient.rpm_status_value.trim() === '';
        }
        return patient.rpm_status_value === searchTerm;
      });
    }

    // Apply multi-select status filters (local filtering)
    if (selectedCCMStatuses.length > 0) {
      filtered = filtered.filter(patient =>
        selectedCCMStatuses.includes(patient.ccm_status_value)
      );
    }

    if (selectedRPMStatuses.length > 0) {
      filtered = filtered.filter(patient => {
        // Handle "No Status Assigned" case
        if (selectedRPMStatuses.includes('No Status Assigned')) {
          if (!patient.rpm_status_value || patient.rpm_status_value.trim() === '') {
            return true;
          }
        }

        // Check for exact match and also check for potential variations
        const exactMatch = selectedRPMStatuses.includes(patient.rpm_status_value);

        // Check for variations of "Consented (In-Person)"
        const normalizedPatientStatus = patient.rpm_status_value?.trim();
        const hasConsentedInPersonMatch = selectedRPMStatuses.some(selectedStatus => {
          const normalizedSelectedStatus = selectedStatus?.trim();
          return normalizedPatientStatus === normalizedSelectedStatus ||
            (normalizedSelectedStatus === 'Consented (In-Person)' &&
              (normalizedPatientStatus === 'Consented(In-Person)' ||
                normalizedPatientStatus === 'Consented (in-Person)' ||
                normalizedPatientStatus === 'Consented(in-Person)' ||
                normalizedPatientStatus.toLowerCase() === 'consented (in-person)'));
        });

        return exactMatch || hasConsentedInPersonMatch;
      });
    }

    return filtered;
  };

  const filteredPatients = getFilteredPatients();

  // Calculate pagination
  const totalPages = Math.ceil(filteredPatients.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = filteredPatients.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-white">
      <UnifiedHeader
        title={title}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
        userType="PE"
        showFollowUp={false}
        showDeferred={false}
        showRefreshButton={true}
        onRefreshClick={fetchPatients}
        isRefreshing={isLoading}
      />
      
      {/* Search and Filter Bar - CS Style */}
      <div className="bg-white border-b px-6 py-4">
        {/* Status Selection Areas - Using common StatusSelectionComponent */}
        <StatusSelectionComponent
          searchBy={searchBy}
          ccmStatusOptions={ccmStatusOptions}
          rpmStatusOptions={rpmStatusOptions}
          selectedCCMStatuses={selectedCCMStatuses}
          selectedRPMStatuses={selectedRPMStatuses}
          onCCMStatusChange={handleCCMStatusChange}
          onRPMStatusChange={handleRPMStatusChange}
          onResetCCMStatuses={() => setSelectedCCMStatuses([])}
          onResetRPMStatuses={() => setSelectedRPMStatuses([])}
          onResetAllStatuses={() => {
            setSelectedCCMStatuses([]);
            setSelectedRPMStatuses([]);
          }}
        />
      </div>

      {/* Main Content */}
      <main className="p-6">
        <Card className="bg-white shadow-sm">
          <CardContent className="p-0">
            {isLoading || clinicIdsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={fetchPatients}>
                  Retry
                </Button>
              </div>
            ) : filteredPatients.length === 0 ? (
              <div className="p-12 text-center">
                <h3 className="text-xl font-medium text-gray-900 mb-4">
                  {patients.length === 0 
                    ? "Congrats! You've cleared all the assigned patients for today."
                    : "No patients found matching the current filters."
                  }
                </h3>
              </div>
            ) : (
              <Table id={`pe-${type}-patients-table`} data-testid={`pe-${type}-patients-table`}>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="text-sm font-medium text-gray-700">Last Appointment</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Name</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Date of Birth</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Phone</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Provider</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Clinic Name</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">CCM Status</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">RPM Status</TableHead>
                    <TableHead className="text-sm font-medium text-gray-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentRecords.map((patient) => (
                    <TableRow key={patient.pep_patient_id} className="hover:bg-gray-50">
                      <TableCell className="text-sm text-gray-600">
                        {formatAppointmentDate(patient.consented_appointment_date_time, patient.patient_clinic_timezone || 'US/Eastern')}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-gray-900">{patient.full_name}</TableCell>
                      <TableCell className="text-sm text-gray-600">{formatDOB(patient.dob)}</TableCell>
                      <TableCell className="text-sm text-gray-600">{patient.phone_number || 'N/A'}</TableCell>
                      <TableCell className="text-sm text-gray-600">{patient.provider_name}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className={badgeStyles.clinic}>
                          {patient.clinic_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${getCCMStatusStyle(patient.ccm_status_value || '')}`}>
                          {patient.ccm_status_value}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${getRPMStatusStyle(patient.rpm_status_value || '')}`}>
                          {patient.rpm_status_value || 'No Status Assigned'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Button variant="outline" className="h-8 w-8" onClick={() => handlePatientAction(patient)}>
                          <SquarePen className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Common Pagination Component */}
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          recordsPerPage={recordsPerPage}
          totalRecords={filteredPatients.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          className="mt-6"
        />
      </main>
    </div>
  );
};

export default PEPatientList;